import { useCallback, useRef, useState } from 'react';
import { AlertTriangle, Fingerprint, ImageUp, Loader, ScanSearch, UserCheck, UserX } from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { PlayerProfile } from '../PlayerProfile';
import { staffManagerApiService } from '../../services/staffManagerApi';
import { matchDigest } from '../../utils/screenWatermark';
import {
  quietestRegion,
  readScreenMark,
  readScreenMarkDeep,
  type DeepReadOutcome,
  type Grade,
  type RasterImage,
  type ReadResult,
} from '../../utils/screenWatermarkRead';
import { cn } from '../../utils/cn';
import type { StaffDirectoryEntry } from '../../types/staffManager';

/**
 * Reads a leaked screenshot back to the account that took it.
 *
 * Everything the panel renders carries a faint repeating mark holding a digest of the viewer's
 * Tesseract subject. Drop a clipping here and this finds the mark, recovers the digest and hashes
 * the staff directory looking for the account it came from.
 *
 * What comes back is graded, and the grade is shown before anything else. A confirmed or likely
 * read names the account — or the accounts, since the digest is short enough that two people on a
 * roster can share one, and that is an ambiguity to show rather than a choice to make. An
 * unconfirmed candidate is not a read: it is shown as a digest that decoded and that nothing
 * corroborated, with the roster entries it is consistent with so a person knows whose second
 * sample to ask for, and never as an answer. The wording is the command-line tool's, so the two
 * cannot disagree about what a grade means.
 *
 * Nothing is stored and nothing is sent anywhere: the image is decoded in this tab, and the only
 * request made is the staff directory this panel already reads.
 */

/** Whether the staff directory could be fetched: without it a digest cannot be turned into anyone. */
type Directory = 'read' | 'unavailable';

type State =
  | { phase: 'idle' }
  | { phase: 'reading'; step: string }
  | { phase: 'done'; read: DeepReadOutcome; matches: StaffDirectoryEntry[]; directory: Directory; preview: string }
  | { phase: 'error'; message: string };

const MAX_EDGE = 4000;
/**
 * Longest edge up to which the retries — the pitch-guided and fixed rescales — run on the whole
 * image. Past it they run on the quietest window instead: a rescale at ×3.5 of a full-size capture
 * is more raster than a tab should hold, and a deep read that finds nothing already costs about ten
 * seconds at this size.
 */
const DEEP_EDGE = 1024;
/** The window the retries fall back to on a larger image: the reader's own default. */
const QUIET_WINDOW = 512;

/** Lets the step label paint before the next synchronous stretch of decoding. */
const painted = () => new Promise<void>((resolve) => { requestAnimationFrame(() => setTimeout(resolve, 0)); });

/** The reader's own preference between two candidates: the stronger screen, then fewer cells put back, then the wider separation. */
const preferred = (left: ReadResult, right: ReadResult): ReadResult =>
  (right.version - left.version || left.errors - right.errors || right.confidence - left.confidence) <= 0 ? left : right;

/**
 * The reader's deep read, kept within what a tab can afford. A small image gets the whole thing.
 * A large one gets the plain read as supplied and then the deep read of its quietest window, with
 * the attempt labels saying so, and the best candidate either pass turned up rides on the failure
 * the way the reader's own deep read carries one.
 */
async function trace(raster: RasterImage, step: (label: string) => void): Promise<DeepReadOutcome> {
  if (Math.max(raster.width, raster.height) <= DEEP_EDGE) {
    step('Searching every alignment, then every rescale…');
    await painted();
    return readScreenMarkDeep(raster, { quietWindow: QUIET_WINDOW });
  }

  step('Searching every alignment…');
  await painted();
  const plain = readScreenMark(raster);
  if (plain.ok) return { outcome: plain, via: 'as supplied', attempts: ['as supplied'] };

  step(`Nothing on a whole-pixel pitch. Retrying the quietest ${QUIET_WINDOW}px window at every rescale…`);
  await painted();
  const within = (label: string) => `quietest ${QUIET_WINDOW}px window, ${label}`;
  const deep = readScreenMarkDeep(quietestRegion(raster, QUIET_WINDOW), { quietWindow: 0 });
  const attempts = ['as supplied', ...deep.attempts.map(within)];
  if (deep.outcome.ok) return { outcome: deep.outcome, via: within(deep.via), attempts };

  let best = plain.candidate ? { candidate: plain.candidate, via: 'as supplied' } : null;
  if (deep.outcome.candidate) {
    const found = { candidate: deep.outcome.candidate, via: within(deep.candidateVia ?? 'as supplied') };
    best = best && preferred(best.candidate, found.candidate) === best.candidate ? best : found;
  }
  return {
    outcome: best ? { ...plain, candidate: best.candidate } : plain,
    via: deep.via,
    attempts,
    ...(best ? { candidateVia: best.via } : {}),
  };
}

const GRADE_TONE: Record<Grade, string> = {
  confirmed: 'badge-success',
  likely: 'badge-info',
  unconfirmed: 'badge-warning',
};

/** What a grade means, in the reader's terms, so the word never stands on its own. */
function meaning(result: ReadResult): string {
  if (result.grade === 'confirmed') return 'Read exactly and corroborated by the rest of the image.';
  if (result.grade === 'likely') {
    return result.corroboration === 'none'
      ? 'The forty-bit screen alone; nothing else in the image corroborated it.'
      : 'Tiles agreed on their own, but the fold did not read clean, so this stops short of confirmed.';
  }
  return 'A decode nothing corroborated; no account is named.';
}

const GradeBadge = ({ grade }: { grade: Grade }) => (
  <span className={cn('badge badge-dot capitalize', GRADE_TONE[grade])}>{grade}</span>
);

/** What the grade rests on, in the command-line tool's words: cells put back, what corroborated it, how the tiles voted alone. */
function evidence(result: ReadResult): string[] {
  const lines = [
    result.errors
      ? `${result.errors} cell${result.errors === 1 ? '' : 's'} put back (head ${result.units.head}, word A ${result.units.a}, word B ${result.units.b})`
      : 'every cell exact',
  ];
  if (result.corroboration !== 'none') lines.push(`corroborated by ${result.corroboration}`);
  const { votes } = result;
  lines.push(`tiles on their own: ${votes.for} for, ${votes.against} against, ${votes.abstained} abstained, of ${votes.of}`);
  if (result.restitch?.corrected) lines.push(`${result.restitch.corrected} cells restitched across the voting tiles`);
  return lines;
}

const personName = (entry: StaffDirectoryEntry): string => `${entry.username}${entry.email ? ` <${entry.email}>` : ''}`;

/** The command-line tool's caveat for an unconfirmed candidate, word for word, so the panel cannot soften it. */
function caveat(candidate: ReadResult, people: StaffDirectoryEntry[], directory: Directory): string {
  const decoded = candidate.errors ? `with ${candidate.errors} corrected cell${candidate.errors === 1 ? '' : 's'}` : 'exactly';
  let consistent = '';
  if (directory === 'read') {
    consistent = people.length
      ? `; consistent with ${people.map(personName).join(', ')}`
      : '; consistent with nobody on the current staff directory';
  }
  return `Not a read. The fold decoded ${decoded} and nothing corroborated it${consistent}; get a second sample.`;
}

/** The digest and where it was read from, for a read and for a candidate alike. */
const MarkCard = ({ result, via }: { result: ReadResult; via: string }) => (
  <div className="subcard p-3">
    <div className="eyebrow mb-1">Mark</div>
    <div className="flex flex-wrap items-baseline gap-x-2">
      <code className="text-sm font-mono text-white break-all">{result.digest}</code>
      <span className="text-xs text-glass-500">format v{result.version}</span>
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-glass-500 tabular-nums">
      <span>read from {via}</span>
      <span aria-hidden>&middot;</span>
      <span>{result.tiles} tiles at {result.scale}&times; pixels</span>
      <span aria-hidden>&middot;</span>
      <span>
        separation {result.confidence.toFixed(1)}
        {result.confidence < 2 && ' (thin — confirm against a larger clipping)'}
      </span>
    </div>
    <ul className="mt-2 space-y-0.5 text-xs text-glass-400 tabular-nums">
      {evidence(result).map((line) => <li key={line}>{line}</li>)}
    </ul>
  </div>
);

export const ScreenMarkReader = () => {
  const [state, setState] = useState<State>({ phase: 'idle' });
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const analyse = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setState({ phase: 'error', message: 'That is not an image. Paste or drop a screenshot.' });
      return;
    }
    setState({ phase: 'reading', step: 'Opening the image…' });
    try {
      const url = URL.createObjectURL(file);
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('That image could not be opened.'));
        element.src = url;
      });

      if (image.width > MAX_EDGE || image.height > MAX_EDGE) {
        URL.revokeObjectURL(url);
        setState({ phase: 'error', message: 'That image is very large. Crop it to the area of interest and try again — a few hundred pixels is enough.' });
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('This browser would not give up a canvas to read the image with.');
      context.drawImage(image, 0, 0);
      const raster = context.getImageData(0, 0, canvas.width, canvas.height);

      const read = await trace(
        { data: raster.data, width: raster.width, height: raster.height },
        (step) => setState({ phase: 'reading', step }),
      );

      // Every account the digest could belong to. A read is walked to name them; an unconfirmed
      // candidate is walked too, so a person knows whose second sample to ask for — but it is
      // presented as what it is, and never as the answer.
      const { outcome } = read;
      const mark = outcome.ok ? outcome : outcome.candidate;
      let matches: StaffDirectoryEntry[] = [];
      let directory: Directory = 'read';
      if (mark) {
        try {
          const response = await staffManagerApiService.getDirectory();
          matches = matchDigest(mark, response.staff, (entry) => entry.user_id);
        } catch {
          // The digest is still worth showing; naming the account is what needs the directory.
          directory = 'unavailable';
        }
      }

      setState({ phase: 'done', read, matches, directory, preview: url });
    } catch (caught) {
      setState({ phase: 'error', message: caught instanceof Error ? caught.message : 'That image could not be read.' });
    }
  }, []);

  const fromClipboard = useCallback((event: React.ClipboardEvent) => {
    const file = [...event.clipboardData.files].find((entry) => entry.type.startsWith('image/'));
    if (file) { event.preventDefault(); void analyse(file); }
  }, [analyse]);

  const reset = useCallback(() => {
    setState((current) => {
      if (current.phase === 'done') URL.revokeObjectURL(current.preview);
      return { phase: 'idle' };
    });
  }, []);

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Trace a screenshot"
        icon={Fingerprint}
        description="Drop a screenshot to trace its account. Security dots and accessibility lines are detected automatically."
      />

      <div
        onPaste={fromClipboard}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file) void analyse(file);
        }}
        tabIndex={0}
        role="button"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click(); }}
        className={cn(
          'rounded-card border border-dashed p-6 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-accent-400/40',
          dragging ? 'border-accent-400/60 bg-accent-500/10' : 'border-glass-200/20 hover:bg-glass-200/[0.04]',
        )}
      >
        <ImageUp className="w-6 h-6 mx-auto text-glass-400" />
        <p className="text-sm text-white mt-2">Drop a screenshot, paste one, or click to choose</p>
        <p className="text-xs text-glass-500 mt-1">
          It is read in this tab. The image is never uploaded.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => { const file = event.target.files?.[0]; if (file) void analyse(file); }}
        />
      </div>

      {state.phase === 'reading' && (
        <div className="mt-4 flex items-center gap-2 text-sm text-glass-300" aria-live="polite">
          <Loader className="w-4 h-4 animate-spin shrink-0" />
          {state.step}
        </div>
      )}

      {state.phase === 'error' && (
        <div className="alert alert-danger mt-4" role="alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{state.message}</span>
        </div>
      )}

      {state.phase === 'done' && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-4">
          <img
            src={state.preview}
            alt="The clipping that was analysed"
            className="rounded-lg border border-glass-200/10 max-h-40 w-auto object-contain bg-dark-900/40"
          />

          <div className="min-w-0 space-y-3">
            {state.read.outcome.ok ? (
              <>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <GradeBadge grade={state.read.outcome.grade} />
                  <span className="text-sm text-glass-300">{meaning(state.read.outcome)}</span>
                </div>

                <MarkCard result={state.read.outcome} via={state.read.via} />

                {state.directory === 'unavailable' ? (
                  <div className="alert alert-warning">
                    <UserX className="w-4 h-4 shrink-0" />
                    <span>
                      The staff directory could not be loaded, so the mark could not be turned into an account.
                      The digest above is still the answer; try again when the directory is back.
                    </span>
                  </div>
                ) : state.matches.length > 0 ? (
                  <div className="subcard p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-white">
                      <UserCheck className="w-4 h-4 text-green-400 shrink-0" />
                      {state.matches.length === 1
                        ? 'Taken by'
                        : `Taken by one of ${state.matches.length} accounts that share this digest`}
                    </div>
                    {state.matches.length > 1 && (
                      <p className="text-xs text-glass-400">
                        The mark is a short digest, and these accounts share it. That is an ambiguity between them,
                        not a wrong name: get a second sample to tell them apart.
                      </p>
                    )}
                    {state.matches.map((match) => (
                      <div key={match.user_id} className="space-y-1">
                        {match.minecraft_uuid
                          ? <PlayerProfile uuid={match.minecraft_uuid} name={match.minecraft_username ?? undefined} />
                          : <span className="text-sm text-white">{match.username}</span>}
                        <dl className="text-xs text-glass-400 space-y-0.5">
                          <div className="flex gap-1.5"><dt className="text-glass-500">Tesseract</dt><dd className="font-mono break-all">{match.username}</dd></div>
                          {match.email && <div className="flex gap-1.5"><dt className="text-glass-500">Email</dt><dd className="break-all">{match.email}</dd></div>}
                        </dl>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    <UserX className="w-4 h-4 shrink-0" />
                    <span>
                      The mark read, but no account on the current staff directory hashes to it.
                      Somebody who has since left would not be on it.
                    </span>
                  </div>
                )}
              </>
            ) : state.read.outcome.candidate ? (
              <>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <GradeBadge grade={state.read.outcome.candidate.grade} />
                  <span className="text-sm text-glass-300">{meaning(state.read.outcome.candidate)}</span>
                </div>

                <div className="alert alert-warning" role="status">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span>{caveat(state.read.outcome.candidate, state.matches, state.directory)}</span>
                </div>

                <MarkCard result={state.read.outcome.candidate} via={state.read.candidateVia ?? state.read.via} />
              </>
            ) : (
              <div className="alert alert-warning" role="status">
                <UserX className="w-4 h-4 shrink-0" />
                <div className="space-y-1">
                  <div>{state.read.outcome.reason}</div>
                  <div className="text-xs opacity-80">
                    Finding nothing does not clear the screenshot: scribbling over a marked page destroys the read
                    along with the mark, and a tampered marked image looks the same as one that was never marked.
                  </div>
                  <div className="text-xs opacity-80">Tried: {state.read.attempts.join(', ')}.</div>
                </div>
              </div>
            )}

            <button type="button" className="glass-button-secondary" onClick={reset}>
              <ScanSearch className="w-4 h-4" />
              Read another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
